import { Button } from '@creditornot/cb-button';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { searchUsers } from 'apis/restaurant-api/merchant-admin/user/api';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { useIsMobile } from 'common/hooks/useIsMobile';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';
import { CampaignsQueryKeys } from 'modules/campaigns/campaignsQueryKeys';
import { TERMS_AND_CONDITIONS_LINK } from 'modules/campaigns/constants';
import { useCampaignModuleContext } from 'modules/campaigns/context';
import { useAdCampaignMutations } from 'modules/campaigns/hooks/useAdCampaignMutations';
import { useCampaignsRoutes } from 'modules/campaigns/hooks/useCampaignsRoutes';
import { AdCampaignType, CampaignStatus } from 'modules/campaigns/types';
import { TestIds } from 'modules/campaigns/utils/testUtils';

import CampaignFormItem from '../../CampaignFormItem';
import CampaignSidebarLayout from '../../CampaignSidebarLayout';
import CopyCampaignIdButton from '../../CopyCampaignIdButton';
import { HelperText } from '../../HelperText';
import { AdsCampaignWeeklyBudgetDetails } from '../../ad-campaign/budget-section/AdsCampaignWeeklyBudgetDetails';
import { AgreementsDetails } from '../../ad-campaign/budget-section/agreements-section/AgreementsDetails';
import { AdCampaignPreview } from '../../ad-campaign/preview-section/AdCampaignPreview';
import { AdCampaignScheduleDetails } from '../../ad-campaign/schedule-section/AdCampaignScheduleDetails';

export type AdCampaignDetailsProps = {
  venueId: string;
  campaign: AdCampaignType;
  venueDetails?: Pick<Venue, 'name' | 'short_description' | 'listimage'>;
};

const FooterWrapper = styled.div<{ isMobile?: boolean }>`
  display: flex;
  gap: 16px;
  justify-content: space-between;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  flex: 1;
`;

export const AdCampaignDetails = ({ venueId, campaign, venueDetails }: AdCampaignDetailsProps) => {
  const { currency } = useCampaignModuleContext();
  const history = useHistory();
  const campaignsRoutes = useCampaignsRoutes();
  const { data: author, isFetching: isLoadingAuthor } = useQuery(
    CampaignsQueryKeys.GET_AUTHOR(campaign.created_by),
    async () => {
      const { users } = await searchUsers(campaign.created_by);
      return users.find(user => user.id === campaign.created_by);
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!campaign.created_by,
    },
  );

  const { stop, restart } = useAdCampaignMutations({ venueIds: [venueId] });

  const { formatMessage } = useIntl();

  const isMobile = useIsMobile();
  const buttonsSize = isMobile ? 'large' : 'small';

  const isCampaignStopped = campaign.status === CampaignStatus.STOPPED;

  const handleStop = () => stop.mutate(campaign.id);

  const handleRestart = () => restart.mutate(campaign.id);

  return (
    <CampaignSidebarLayout
      preview={<AdCampaignPreview venueDetails={venueDetails} />}
      tooltip="campaigns.sidebar-preview.ads.tooltip"
      isFloatingBannerEnabled
      infoBannerContent={{
        title: formatMessage({ id: 'campaigns.ads.info-banner.title' }),
        text: formatMessage({ id: 'campaigns.ads.info-banner.text' }),
        campaignId: campaign.id,
      }}
    >
      <CampaignFormItem
        data-testid="budget-field-item"
        title={<FormattedMessage id="campaigns.ads.budget.weekly-budget" />}
        tooltip={{
          description: <FormattedMessage id="campaigns.form.budget.tooltip" />,
        }}
        value={
          <>
            <AdsCampaignWeeklyBudgetDetails
              bidAmount={
                // bid strategy is in lowercase after saving changes in the edit page so use toUpperCase here, after reload it is in uppercase again
                campaign.bid_strategy.toUpperCase() === 'CUSTOM' && campaign.bid_amount
                  ? convertMonetaryIntToDecimal(campaign.bid_amount, currency)
                  : undefined
              }
              budgetAmount={convertMonetaryIntToDecimal(campaign.weekly_budget_amount, currency)}
            />
            <HelperText text={formatMessage({ id: 'campaigns.ads.budget.vat-text' })} />
          </>
        }
      />
      <AdCampaignScheduleDetails campaignId={campaign.id} startDate={campaign.start_date} endDate={campaign.end_date} />
      <AgreementsDetails
        agreements={[
          <FormattedMessage
            id="campaigns.agreements.terms-and-conditions-statement"
            values={{
              link: (
                <a href={TERMS_AND_CONDITIONS_LINK} target="_blank" rel="noreferrer">
                  <FormattedMessage id="campaigns.agreements.terms-and-conditions-link" />
                </a>
              ),
            }}
          />,
        ]}
        isLoading={isLoadingAuthor}
        author={author}
        isCreatedByWolt={author?.email.endsWith('@wolt.com')} // TODO: @jjanii replade with campaign.created_by_wolt when it will be available
      />
      {campaign.status !== CampaignStatus.COMPLETED && (
        <FooterWrapper isMobile={isMobile}>
          {isCampaignStopped ? (
            <Button
              variant="blue"
              size={buttonsSize}
              onClick={handleRestart}
              loading={restart.isLoading}
              disabled={restart.isLoading}
            >
              <FormattedMessage id="campaigns.restart-campaign" />
            </Button>
          ) : (
            <Button
              variant="lightRed"
              size={buttonsSize}
              onClick={handleStop}
              loading={stop.isLoading}
              disabled={stop.isLoading}
            >
              <FormattedMessage id="campaigns.stop-campaign" />
            </Button>
          )}
          <Button
            variant="lightBlue"
            size={buttonsSize}
            onClick={() => history.push(`${campaignsRoutes.edit(campaign.id)}?campaignType=Ads`)}
          >
            <FormattedMessage id="campaigns.edit-campaign" />
          </Button>
        </FooterWrapper>
      )}
      <CopyCampaignIdButton dataTestId={TestIds.DETAILS.ID.WRAPPER} campaignId={campaign.id} />
    </CampaignSidebarLayout>
  );
};
